import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  GET_ORDERS_HISTORY,
  GET_PRODUCTS_LIST,
  LINK_JOB_TO_ORDER_ITEM,
} from '../graphql/orders';
import { GET_ALL_PROPERTIES } from '../graphql/queries';
import TeamMemberPagination from '../components/team-members/TeamMemberPagination';
import AddJobModal from '../components/AddJobModal';

const ITEMS_PER_PAGE = 20;

const STATUS_OPTIONS = [
  { value: 'PAID', label: 'Paid' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none" stroke="currentColor" viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const OrderDetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value || 'N/A'}</p>
  </div>
);

const JobStatusBadge = ({ item, onCreateJob }) => {
  if (item.jobId && item.job) {
    const isCompleted = item.job.isCompleted;
    return (
      <div className="flex items-center gap-2">
        <a
          href={`/dashboard/jobs/${item.job.id}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
            isCompleted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          {isCompleted ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {isCompleted ? 'Completed' : item.job.status}
        </a>
        {item.job.assignedTo && (
          <span className="text-xs text-gray-500">{item.job.assignedTo.name}</span>
        )}
      </div>
    );
  }

  if (item.product?.serviceTypeId) {
    return (
      <button
        onClick={onCreateJob}
        className="inline-flex items-center gap-1.5 text-sm bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 rounded transition-colors"
        aria-label={`Create job for ${item.title}`}
        tabIndex={0}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
        </svg>
        Create Job
      </button>
    );
  }

  return <span className="text-xs text-gray-400">No job type set</span>;
};

const StatusBadge = ({ status }) => {
  const styles = {
    PAID: 'bg-green-100 text-green-700',
    APPROVED: 'bg-sky-100 text-sky-700',
    REJECTED: 'bg-red-100 text-red-700',
    FAILED: 'bg-red-100 text-red-700',
  };
  const labels = {
    PAID: 'Paid',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    FAILED: 'Failed',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {labels[status] || status}
    </span>
  );
};

export default function OrdersHistory() {
  const navigate = useNavigate();
  const [expandedOrders, setExpandedOrders] = useState({});
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [jobModalContext, setJobModalContext] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: 'info' });

  const [filters, setFilters] = useState({
    page: 1,
    statuses: [],
    propertyId: '',
    productId: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  });

  const buildQueryInput = () => {
    const input = { page: filters.page, limit: ITEMS_PER_PAGE };
    if (filters.statuses.length) input.statuses = filters.statuses;
    if (filters.propertyId) input.propertyId = filters.propertyId;
    if (filters.productId) input.productId = filters.productId;
    if (filters.dateFrom) input.dateFrom = filters.dateFrom;
    if (filters.dateTo) input.dateTo = filters.dateTo;
    if (filters.search) input.search = filters.search;
    return input;
  };

  const { loading, error, data, refetch } = useQuery(GET_ORDERS_HISTORY, {
    variables: { input: buildQueryInput() },
    fetchPolicy: 'cache-and-network',
  });

  const { data: propertiesData } = useQuery(GET_ALL_PROPERTIES, {
    variables: { limit: 500 },
  });

  const { data: productsData } = useQuery(GET_PRODUCTS_LIST);

  const [linkJobToOrderItem] = useMutation(LINK_JOB_TO_ORDER_ITEM, {
    onCompleted: () => {
      refetch();
      showNotificationMessage('Job created and linked to order item', 'success');
    },
    onError: (err) => {
      showNotificationMessage(`Error linking job: ${err.message}`, 'error');
    },
  });

  const showNotificationMessage = (message, type) => {
    setNotification({ message, type });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 6000);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleClearFilters = () => {
    setFilters({ page: 1, statuses: [], propertyId: '', productId: '', dateFrom: '', dateTo: '', search: '' });
  };

  const toggleAccordion = (orderId) => {
    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const handleCreateJob = (order, item) => {
    setJobModalContext({ order, item });
    setIsJobModalOpen(true);
  };

  const handleJobCreated = async (jobData) => {
    if (jobModalContext?.item?.id && jobData?.id) {
      await linkJobToOrderItem({
        variables: { orderItemId: jobModalContext.item.id, jobId: jobData.id },
      });
    }
    setIsJobModalOpen(false);
    setJobModalContext(null);
  };

  const buildJobNotes = (order, item) => {
    const parts = [item.title];
    if (item.variation?.name) parts.push(item.variation.name);
    if (order.notes) parts.push(`Guest Notes: ${order.notes}`);
    return parts.join('\n');
  };

  const hasActiveFilters = filters.statuses.length || filters.propertyId || filters.productId || filters.dateFrom || filters.dateTo || filters.search;

  const properties = propertiesData?.allProperties?.items || [];
  const products = productsData?.products || [];
  const result = data?.ordersHistory;
  const orders = result?.items || [];

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/orders')}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
            aria-label="Back to orders"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-semibold">Order History</h1>
        </div>
        {result && (
          <span className="text-sm text-gray-500">{result.total} total orders</span>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Search */}
          <div className="lg:col-span-2">
            <label htmlFor="search" className="block text-xs font-medium text-gray-500 mb-1">Guest Name</label>
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                id="search"
                type="text"
                placeholder="Search by guest name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              id="status"
              value={filters.statuses[0] || ''}
              onChange={(e) => handleFilterChange('statuses', e.target.value ? [e.target.value] : [])}
              className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Property */}
          <div>
            <label htmlFor="property" className="block text-xs font-medium text-gray-500 mb-1">Property</label>
            <select
              id="property"
              value={filters.propertyId}
              onChange={(e) => handleFilterChange('propertyId', e.target.value)}
              className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
            >
              <option value="">All Properties</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Product */}
          <div>
            <label htmlFor="product" className="block text-xs font-medium text-gray-500 mb-1">Product</label>
            <select
              id="product"
              value={filters.productId}
              onChange={(e) => handleFilterChange('productId', e.target.value)}
              className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
            >
              <option value="">All Products</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label htmlFor="dateFrom" className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
            <input
              id="dateFrom"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label htmlFor="dateTo" className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
            <input
              id="dateTo"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={handleClearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading && !data ? (
          <div className="p-8 text-center text-gray-500">Loading orders...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">Error loading orders: {error.message}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="p-4 w-10"></th>
                    <th className="p-4 text-sm font-medium text-gray-600">Item Name</th>
                    <th className="p-4 text-sm font-medium text-gray-600">Variant</th>
                    <th className="p-4 text-sm font-medium text-gray-600">Request Date</th>
                    <th className="p-4 text-sm font-medium text-gray-600">Property</th>
                    <th className="p-4 text-sm font-medium text-gray-600">Total</th>
                    <th className="p-4 text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.length > 0 ? (
                    orders.map(order => {
                      const isExpanded = expandedOrders[order.id];
                      return (
                        <React.Fragment key={order.id}>
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleAccordion(order.id)}
                          >
                            <td className="p-4"><ChevronIcon isOpen={isExpanded} /></td>
                            <td className="p-4 text-sm font-medium">
                              {order.items.map(i => i.title).join(', ')}
                            </td>
                            <td className="p-4 text-sm text-gray-600">
                              {order.items.map(i => i.variation?.name || '—').join(', ')}
                            </td>
                            <td className="p-4 text-sm">{format(new Date(order.createdAt), 'MMM dd, yyyy')}</td>
                            <td className="p-4 text-sm">{order.property?.name || 'N/A'}</td>
                            <td className="p-4 text-sm font-medium">${order.total.toFixed(2)}</td>
                            <td className="p-4"><StatusBadge status={order.status} /></td>
                          </tr>

                          {isExpanded && (
                            <tr>
                              <td colSpan="7" className="bg-gray-50 px-8 py-5">
                                <div className="space-y-4">
                                  {/* Reservation / Guest Details */}
                                  {order.reservation ? (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Reservation Details</h4>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                        <OrderDetailRow label="Confirmation #" value={order.reservation.streamline_reservation_id} />
                                        <OrderDetailRow label="Guest Name" value={order.reservation.guest_name || order.billingName} />
                                        <OrderDetailRow label="Email" value={order.reservation.email || order.billingEmail} />
                                        <OrderDetailRow label="Phone" value={order.reservation.mobile_phone || order.billingPhone} />
                                        <OrderDetailRow
                                          label="Check-in"
                                          value={order.reservation.check_in ? format(new Date(order.reservation.check_in), 'MMM dd, yyyy') : null}
                                        />
                                        <OrderDetailRow
                                          label="Check-out"
                                          value={order.reservation.check_out ? format(new Date(order.reservation.check_out), 'MMM dd, yyyy') : null}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Guest Details</h4>
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                        <OrderDetailRow label="Guest Name" value={order.billingName} />
                                        <OrderDetailRow label="Email" value={order.billingEmail} />
                                        <OrderDetailRow label="Phone" value={order.billingPhone} />
                                      </div>
                                    </div>
                                  )}

                                  {/* Payment Info */}
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                      <OrderDetailRow label="Total" value={`$${order.total.toFixed(2)}`} />
                                      <OrderDetailRow
                                        label="Payment Status"
                                        value={order.status === 'PAID' ? 'Paid via Stripe' : order.status === 'REJECTED' ? 'Rejected / Refunded' : 'Approved (no payment)'}
                                      />
                                      {order.paidAt && (
                                        <OrderDetailRow label="Paid At" value={format(new Date(order.paidAt), 'MMM dd, yyyy h:mm a')} />
                                      )}
                                      {order.approvedAt && (
                                        <OrderDetailRow label="Approved At" value={format(new Date(order.approvedAt), 'MMM dd, yyyy h:mm a')} />
                                      )}
                                    </div>
                                  </div>

                                  {/* Rejection Reason */}
                                  {order.status === 'REJECTED' && order.rejectionReason && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rejection Reason</h4>
                                      <p className="text-sm text-red-700 bg-red-50 rounded-lg border border-red-200 p-3">{order.rejectionReason}</p>
                                    </div>
                                  )}

                                  {/* Order Items with Job Status */}
                                  {order.status !== 'REJECTED' && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Items & Jobs</h4>
                                      <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                        {order.items.map(item => (
                                          <div key={item.id} className="flex items-center justify-between p-4">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-900">{item.title}</span>
                                                {item.quantity > 1 && (
                                                  <span className="text-xs text-gray-400">x{item.quantity}</span>
                                                )}
                                                <span className="text-sm text-gray-500">${(item.price * item.quantity).toFixed(2)}</span>
                                              </div>
                                              {item.variation?.name && (
                                                <p className="text-sm text-gray-500 mt-0.5">{item.variation.name}</p>
                                              )}
                                            </div>
                                            <div onClick={(e) => e.stopPropagation()}>
                                              <JobStatusBadge
                                                item={item}
                                                onCreateJob={() => handleCreateJob(order, item)}
                                              />
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Notes */}
                                  {order.notes && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Guest Notes</h4>
                                      <p className="text-sm text-gray-700 bg-white rounded-lg border border-gray-200 p-3">{order.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-gray-500">
                        {hasActiveFilters ? 'No orders match your filters' : 'No orders found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {result && result.totalPages > 1 && (
              <TeamMemberPagination
                currentPage={result.page}
                totalPages={result.totalPages}
                totalResults={result.total}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Create Job Modal */}
      {jobModalContext && (
        <AddJobModal
          isOpen={isJobModalOpen}
          onClose={() => {
            setIsJobModalOpen(false);
            setJobModalContext(null);
          }}
          onCreateJob={handleJobCreated}
          initialPropertyId={jobModalContext.order.property?.id}
          initialProperty={jobModalContext.order.property}
          initialServiceTypeId={jobModalContext.item.product?.serviceTypeId}
          initialNotes={buildJobNotes(jobModalContext.order, jobModalContext.item)}
          initialScheduledDate={
            jobModalContext.order.reservation?.check_in
              ? format(new Date(jobModalContext.order.reservation.check_in), 'yyyy-MM-dd')
              : undefined
          }
        />
      )}

      {/* Notification */}
      {showNotification && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className={`p-4 rounded-lg shadow-lg max-w-md ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200' :
            notification.type === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex">
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' :
                  notification.type === 'error' ? 'text-red-800' : 'text-blue-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setShowNotification(false)}
                  className={`inline-flex rounded-md p-1.5 ${
                    notification.type === 'success' ? 'text-green-500 hover:bg-green-100' :
                    notification.type === 'error' ? 'text-red-500 hover:bg-red-100' :
                    'text-blue-500 hover:bg-blue-100'
                  }`}
                  aria-label="Dismiss notification"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
